import { Button, csx, IconPlus } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { SCOPE_RESTRICTION_TYPE, type Scope } from 'typings'
import { each, enforce, test } from 'vest'
import {
  useFormStateContext,
  useFormValidation,
  ValidationFn,
} from '../../../providers'
import {
  DEFAULT_INITIAL_EDITORS,
  EMPTY_SCOPE_RESTRICTION,
  SCOPE_RESTRICTION_TYPES,
} from './constants'
import { ScopeRestrictionEditor } from './RestrictionEditor'

function getInitialEditors(scope: Scope): Set<SCOPE_RESTRICTION_TYPE> {
  const editors = Object.entries(scope).reduce((acc, [type, config]) => {
    if (config.items.length) return new Set([...acc, type])

    return acc
  }, new Set<SCOPE_RESTRICTION_TYPE>())

  return editors.size ? editors : DEFAULT_INITIAL_EDITORS
}

interface Props {
  name: string
  resetScope: () => void
}

export function ScopeRestrictionsList(props: Props) {
  const { name, resetScope } = props
  const { form, readOnly } = useFormStateContext()
  const scope: Scope = useWatch({ form, name })

  const [openEditors, setOpenEditors] = useState<Set<SCOPE_RESTRICTION_TYPE>>(
    getInitialEditors(scope)
  )

  useFormValidation('scope-restrictions', {
    fieldName: 'scope',
    validateFn: validationFunction(name, Array.from(openEditors)),
  })

  const availableOptions = Object.values(SCOPE_RESTRICTION_TYPES).filter(
    (type) => !openEditors.has(type.id)
  )

  function getAvailableTypesForEditor(editorType: SCOPE_RESTRICTION_TYPE) {
    return Object.values(SCOPE_RESTRICTION_TYPES).filter(
      (type) =>
        availableOptions.some(({ id }) => id === type.id) ||
        type.id === editorType
    )
  }

  function removeScopeRestriction(type: SCOPE_RESTRICTION_TYPE) {
    form.resetField(`${name}.${type}`, {
      defaultValue: { ...EMPTY_SCOPE_RESTRICTION },
    })

    setOpenEditors((current) => {
      const newEditors = new Set(current)
      newEditors.delete(type)
      return newEditors
    })
  }

  useEffect(() => {
    !openEditors.size && resetScope()
  }, [openEditors.size, resetScope])

  function addScopeRestriction(type?: SCOPE_RESTRICTION_TYPE) {
    const newType = type ?? availableOptions[0].id

    setOpenEditors((current) => new Set([...current, newType]))
  }

  return (
    <>
      {Array.from(openEditors).map((type) => (
        <div key={type} className={csx({ paddingTop: '$space-5' })}>
          <ScopeRestrictionEditor
            baseName={name}
            type={type}
            options={getAvailableTypesForEditor(type)}
            onChangeType={(newType) => {
              removeScopeRestriction(type)
              addScopeRestriction(newType)
            }}
            onDelete={removeScopeRestriction}
          />
        </div>
      ))}

      {availableOptions.length && !readOnly ? (
        <div className={csx({ paddingTop: '$space-4' })}>
          <Button
            variant="secondary"
            icon={<IconPlus />}
            onClick={() => addScopeRestriction()}
          >
            <FormattedMessage id="admin/promotions.form.scope.add-rule" />
          </Button>
        </div>
      ) : null}
    </>
  )
}

function validationFunction(
  baseName: string,
  openEditors: SCOPE_RESTRICTION_TYPE[]
): ValidationFn<Scope> {
  return (scope, { intl: { formatMessage } }) => {
    each(openEditors, (type) => {
      test(
        `${baseName}.${type}.items`,
        formatMessage({
          id: 'admin/promotions.form.validation.select-an-option',
        }),
        () => {
          enforce(scope[type].items.length).gt(0)
        }
      )
    })
  }
}
